/**
 * Whether the two-way SMS conversation entry points may be shown to this user.
 *
 * Two layers, and the SERVER is the gate: the `operatorSmsConversations` flag only decides whether
 * we ask; `sms_conversations_available()` decides the answer (role + feature on, or internal proof
 * with the caller allowlisted). Loading, false and error all mean "do not show".
 */
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { isFeatureEnabled } from "../utils/featureFlags";
import { normalizeRole } from "../utils/roleUtils";
import { smsConversationsAvailable } from "../services/smsConversations.service";

/** The roles the server accepts (plan §3.2); everyone else is never asked. Route guard uses the same set. */
export const SMS_CONVERSATION_ROLES = [
  "super_admin",
  "admin",
  "support_admin",
  "manager",
  "support_agent",
];

export function useSmsConversationsAvailable(): {
  available: boolean;
  loading: boolean;
} {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  const roleOk = SMS_CONVERSATION_ROLES.map(normalizeRole).includes(role);
  const enabled =
    isFeatureEnabled("operatorSmsConversations") && !!user?.id && roleOk;
  const q = useQuery({
    queryKey: ["sms-conversations-available", user?.id],
    queryFn: smsConversationsAvailable,
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
  return {
    available: enabled && q.data === true,
    loading: enabled && q.isLoading,
  };
}
